'use client'

import { useMemo } from 'react'
import {
  calculateLastInteraction,
  useDocumentView,
  useDocumentStatus,
} from '@/document'
import { CONTACT_FREQUENCY_OPTIONS, type ContactFrequency } from '@/entries'
import type { Entry } from '@/document'

export interface OverdueContact {
  person: Entry
  daysSince: number
  frequency: string
}

/**
 * Hook returning people who are overdue for contact based on their
 * contactFrequency setting and last interaction date.
 */
export function usePeopleNudges() {
  const doc = useDocumentView()
  const ready = useDocumentStatus()

  const data = useMemo(() => {
    if (!doc) return { overdueContacts: [] as OverdueContact[], isEmpty: true }

    const now = new Date()

    // Get all non-archived person entries with contactFrequency set
    const people = Object.values(doc.entries).filter(
      (entry) =>
        entry.type === 'person' &&
        !entry.archivedAt &&
        entry.metadata.contactFrequency != null,
    )

    const overdueContacts: OverdueContact[] = []

    for (const person of people) {
      const frequency = person.metadata.contactFrequency as ContactFrequency

      // Find the threshold in days for this frequency
      const option = CONTACT_FREQUENCY_OPTIONS.find(
        (o) => o.value === frequency,
      )
      if (!option) continue

      // Calculate last interaction date
      const lastInteraction = calculateLastInteraction(person.id, doc)

      let daysSince: number
      if (lastInteraction) {
        const lastDate = new Date(lastInteraction).getTime()
        daysSince = Math.floor(
          (now.getTime() - lastDate) / (1000 * 60 * 60 * 24),
        )
      } else {
        // No interactions ever - treat as very overdue
        daysSince = option.days + 1
      }

      if (daysSince > option.days) {
        overdueContacts.push({
          person,
          daysSince,
          frequency,
        })
      }
    }

    // Sort by most overdue first (daysSince - threshold, descending)
    overdueContacts.sort((a, b) => {
      const aThreshold =
        CONTACT_FREQUENCY_OPTIONS.find((o) => o.value === a.frequency)?.days ??
        0
      const bThreshold =
        CONTACT_FREQUENCY_OPTIONS.find((o) => o.value === b.frequency)?.days ??
        0
      return b.daysSince - bThreshold - (a.daysSince - aThreshold)
    })

    return {
      overdueContacts,
      isEmpty: overdueContacts.length === 0,
    }
  }, [doc])

  return {
    ...data,
    isLoading: !ready,
  }
}
